import {useMemo} from "react";
import {Link} from "react-router";
import {Loader2Icon, Plus} from "lucide-react";
import {createSerializer, parseAsInteger, parseAsString, useQueryState} from "nuqs";
import {List, RowComponentProps} from "react-window";
import {useInfiniteLoader} from "react-window-infinite-loader";
import SearchBar from "@/search/SearchBar.tsx";
import {Button} from "@/components/ui/button.tsx";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {CharacterFilter as CharacterFilterPopover} from "@/characters/admin/CharacterFilter.tsx";
import {CharacterSummary} from "@/characters/model/Character.ts";
import CharacterListItem from "@/characters/CharacterListItem.tsx";
import {useCharactersQuery} from "@/characters/CharacterQueries.ts";

const PAGE_SIZE = 50;
const searchParsers = {
    name: parseAsString.withDefault(""),
    type: parseAsString.withDefault(""),
    hostile: parseAsString.withDefault(""),
    min: parseAsInteger.withDefault(0),
    max: parseAsInteger.withDefault(30),
};
const serializeSearch = createSerializer(searchParsers);

export default function CharacterListPanel({selectedId}: {selectedId?: string}) {
    const [name, setName] = useQueryState("name", searchParsers.name);
    const [type, setType] = useQueryState("type", searchParsers.type);
    const [hostile, setHostile] = useQueryState("hostile", searchParsers.hostile);
    const [min, setMin] = useQueryState("min", searchParsers.min);
    const [max, setMax] = useQueryState("max", searchParsers.max);
    const {campaignPath} = useCampaign();
    const filter = useMemo(() => ({
        name,
        type: type || undefined,
        hostile: hostile === "true" ? true : hostile === "false" ? false : undefined,
        min,
        max,
    }), [hostile, max, min, name, type]);
    const query = useCharactersQuery(filter, PAGE_SIZE);
    const search = serializeSearch({name, type, hostile, min, max});
    const characterBasePath = campaignPath("characters");
    const onRowsRendered = useInfiniteLoader({
        isRowLoaded: index => index < query.characters.length,
        loadMoreRows: query.loadMore,
        rowCount: query.rowCount,
        minimumBatchSize: PAGE_SIZE,
    });

    const resetFilters = () => {
        void setType("");
        void setHostile("");
        void setMin(0);
        void setMax(30);
    };

    return (
        <>
            <div className="flex gap-2 p-1">
                <SearchBar className="grow" value={name} onChange={setName}/>
                <CharacterFilterPopover type={type} setType={value => void setType(value)}
                                        hostile={hostile} setHostile={value => void setHostile(value)}
                                        min={min} max={max}
                                        setMinMax={(nextMin, nextMax) => {
                                            void setMin(nextMin);
                                            void setMax(nextMax);
                                        }}
                                        hasFilters={Boolean(type || hostile) || min !== 0 || max !== 30}
                                        reset={resetFilters}/>
            </div>
            <List className="bg-surface"
                  onRowsRendered={onRowsRendered}
                  rowComponent={VirtualCharacterRow}
                  rowCount={query.rowCount}
                  rowHeight={40}
                  rowProps={{characters: query.characters, selectedId, characterBasePath, search}}/>
            <div className="flex items-center justify-between gap-2 px-2 py-1">
                <span className="text-sm text-muted-foreground">
                    {query.isError ? "Charaktere konnten nicht geladen werden" : `${query.totalCount} Ergebnisse`}
                </span>
                <Button size="sm" asChild>
                    <Link to={{pathname: campaignPath("characters/new"), search}}>Charakter <Plus/></Link>
                </Button>
            </div>
        </>
    );
}

function VirtualCharacterRow({index, style, characters, selectedId, characterBasePath, search}: RowComponentProps<{
    characters: CharacterSummary[];
    selectedId?: string;
    characterBasePath: string;
    search: string;
}>) {
    const character = characters[index];
    if (!character) return <div style={style} className="p-3 text-muted-foreground"><Loader2Icon className="animate-spin"/></div>;
    return <CharacterListItem character={character}
                              href={`${characterBasePath}/${character.id}`}
                              search={search}
                              selected={character.id === selectedId}
                              style={style}/>;
}
